import {
  BasePageMetaResolver,
  CmsService,
  ContentPageMetaResolver,
  Page,
  PageRobotsMeta,
  WindowRef
} from '@spartacus/core'
import { Injectable, OnDestroy } from '@angular/core'
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs'
import { distinctUntilChanged, filter, map, switchMap } from 'rxjs/operators'
import { MbNavigationService } from '../navigation/mb-navigation.service'
import { MbConstants } from '../../shared/mb-constants'
import { CmsConverterService } from '../services/cms-converter/cms-converter.service'
import { MbCaasService } from '../services/mb-caas/mb-caas.service'
import { CurrentProductService } from '@spartacus/storefront'
import { CaasIdType } from '../enums/caas-id-types'
import { ObjectUtils } from '../../shared/utils/object-utils'
import { CmsDOMInputParser } from '../../shared/utils/cms-dom-input-parser'
import { CmsCountryService } from '../services/cms-country/cms-country.service'
import { CountryService } from '../../site-context/facade/country.service'
import { MbPageMetaLinkService } from './mb-page-meta-link.service'
import { Router } from '@angular/router'
import { MbBasePageMetaResolver } from './mb-base-page-meta-resolver'
import { MbBreadcrumbService } from '../services/mb-breadcrumb/mb-breadcrumb.service'

@Injectable({
  providedIn: 'root'
})
export class MbContentPageMetaResolver extends ContentPageMetaResolver implements OnDestroy {

  cmsPage = new BehaviorSubject<Page>({})
  isFirstPage = true
  lastUrl: string | undefined
  private subscription = new Subscription()

  constructor (public cmsService: CmsService,
               basePageMetaResolver: BasePageMetaResolver,
               private mbNavigationService: MbNavigationService,
               private caasService: MbCaasService,
               private currentProductService: CurrentProductService,
               private mbBasePageMetaResolver: MbBasePageMetaResolver,
               private domInputParser: CmsDOMInputParser,
               private cmsCountryService: CmsCountryService,
               private countryService: CountryService,
               protected winRef: WindowRef,
               private mbPageMetaLinkService: MbPageMetaLinkService,
               private breadcrumbService: MbBreadcrumbService,
               private router: Router) {
    super(basePageMetaResolver)
  }

  resolveCanonicalUrl (): Observable<string | undefined> {
    return this.mbPageMetaLinkService.setCanonicalUrl()
  }

  resolveTitle (): Observable<string> {
    return combineLatest([
      this.cmsService.getCurrentPage().pipe(distinctUntilChanged()),
      this.countryService.getActive()
    ]).pipe(
      switchMap(([page, activeCountry]) => {
        const metaTitle = page?.properties?.formData?.pt_meta_title?.value?.trim() ?? ''
        const country = this.cmsCountryService.getCountryByISO(activeCountry)
          || this.cmsCountryService.getCountryByISO(MbConstants.FALLBACK_COUNTRY)
        // suffix for title including Miltenyi Biotec and current countries national name
        const suffix = ` | ${MbConstants.PAGE_TITLE_MILTENYI_SUFFIX} | ${country?.nationalName}`
        if (metaTitle.length) {
          // return title with suffix from page if present
          return of(`${metaTitle}${suffix}`)
        }
        return this.resolveBreadcrumbs().pipe(
          map(breadcrumbs => {
              // reverse breadcrumb and concat with |
              const breadcrumbsTitle = breadcrumbs.reverse().map(breadcrumb => breadcrumb.label).join(' | ')
              // return reversed breadcrumb as title with suffix if meta title not present
              return `${breadcrumbsTitle}${suffix}`
            }
          ))
      }),
    )
  }

  resolveBreadcrumbs () {
    return this.cmsService.getCurrentPage().pipe(
      filter(page => page?.page?.formData !== undefined || page?.properties?.formData !== undefined || page?.type !== undefined),
      map(page => this.breadcrumbService.getPathNodes(this.mbNavigationService.resolvedIdMap.value, page?.properties?.previewId?.split('.')?.shift())),
      // Add current page as last breadcrumb without link if not a category page
      map(({ currentPagePreviewId, currentNode, pathNodes }) =>
        this.breadcrumbService.addCurrentPageToPathNodes(pathNodes, currentNode, currentPagePreviewId, null)),
      switchMap(({ pathNodes, currentNode, currentPagePreviewId }) =>
        this.breadcrumbService.getHideAndShortenBreadcrumbsForPathNodes(pathNodes, currentPagePreviewId, currentNode)),
      map(({ pathNodes, currentNode }) => this.breadcrumbService.mapPathNodesToBreadcrumbs(pathNodes, currentNode, null))
    )
  }

  resolveRobots (): Observable<PageRobotsMeta[]> {
    return this.cmsService.getCurrentPage().pipe(
      map(page => {
        const robots = []
        if (this.winRef?.location?.pathname?.split('/')?.[1]?.includes(MbConstants.FALLBACK_COUNTRY)) {
          return [PageRobotsMeta.NOFOLLOW, PageRobotsMeta.NOINDEX]
        }
        if (page?.properties?.formData?.pt_hide_nofollow?.value) {
          robots.push(PageRobotsMeta.NOFOLLOW)
        }
        if (page?.properties?.formData?.pt_hide_search_engine?.value) {
          robots.push(PageRobotsMeta.NOINDEX)
        }
        return robots
      })
    )
  }

  resolveKeywords (): Observable<string | undefined> {
    return this.cmsService.getCurrentPage().pipe(
      map(page => page?.properties?.formData?.pt_meta_keywords?.value)
    )
  }

  resolveDescription (): Observable<string | undefined> {
    let cmsPage: Page
    let countryNationalName: string | undefined
    return combineLatest([this.cmsService.getCurrentPage(), this.countryService.getActive()]).pipe(
      switchMap(([page, country]) => {
        cmsPage = page
        countryNationalName = this.cmsCountryService.getCountryByISO(country)?.nationalName
        return this.caasService.getByIds([page?.properties?.previewId?.split('.')?.shift()], CaasIdType.PAGEID)
      }),
      map(caasPage => {
        const description = cmsPage?.properties?.formData?.pt_meta_description?.value?.trim() || ''
        // return meta description if present
        if (description.length) {
          return `${description} | ${countryNationalName}`
        }
        // get and return text from first none headline text field
        const components = caasPage?.[0]?.page?.children.flatMap((slot: any) => slot?.children ?? []) ?? []
        const firstText = components.flatMap((component: any) => this.getFirstDOMInputsFromPage(component))[0] || ''
        const descriptionText = ObjectUtils.getInnerText(this.domInputParser.parse(firstText))
        return `${descriptionText} | ${countryNationalName}`
      })
    )
  }

  resolveMetaTitle (): Observable<string | undefined> {
    return this.resolveTitle()
  }

  resolveOGImage (image?: string): Observable<string | undefined> {
    return this.cmsService.getCurrentPage().pipe(
      map(page => image || CmsConverterService.mapImage(page?.properties?.formData?.pt_open_graph_image, true)?.original)
    )
  }

  resolveOGTitle (): Observable<string | undefined> {
    return this.cmsService.getCurrentPage().pipe(
      map(page => page?.properties?.formData?.pt_open_graph_title?.value)
    )
  }

  resolveOGDescription (): Observable<string | undefined> {
    return this.cmsService.getCurrentPage().pipe(
      map(page => page?.properties?.formData?.pt_open_graph_description?.value)
    )
  }

  resolveOGType (): Observable<string | undefined> {
    return this.cmsService.getCurrentPage().pipe(
      map(page => page?.properties?.formData?.pt_open_graph_type?.value?.identifier)
    )
  }

  resolvePageMainColor (): Observable<string | undefined> {
    return this.cmsService.getCurrentPage().pipe(
      map(page => page?.formData?.pt_pageMainColor?.value?.identifier ||
        page?.properties?.formData?.pt_pageMainColor?.value?.identifier || 'corporate-orange')
    )
  }

  ngOnDestroy () {
    this.subscription.unsubscribe()
  }

  private getFirstDOMInputsFromPage (object?: any, domInputs: any[] = []) {
    if (!object || !object.formData) {
      return domInputs
    }
    Object.keys(object.formData).every((key: string) => {
      if (object.formData[key]?.fsType === 'CMS_INPUT_DOM' && object.formData[key]?.value?.trim()?.length &&
        !object.formData[key]?.value?.includes('format.h')) {
        domInputs.push(object.formData[key]?.value)
        return false
      }
      if (object.formData[key]?.fsType === 'FS_CATALOG') {
        object.formData[key].value.forEach((item: any) => this.getFirstDOMInputsFromPage(item, domInputs))
        this.getFirstDOMInputsFromPage(object.formData[key], domInputs)
      }
      return true
    })
    return domInputs
  }
}

